import { motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useMemo } from "react";
import { useLowPower } from "@/hooks/useLowPower";

/**
 * Static star field for touch / low-power devices: same visual texture, but
 * zero animation frames and no pointer listeners. 75 individually animated
 * nodes was the single heaviest decoration on mobile.
 */
const StaticStars = () => {
  const stars = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        x: (i * 37 + 17) % 100,
        y: (i * 53 + 23) % 100,
        size: 1 + ((i * 7) % 3) * 0.5,
        opacity: 0.25 + ((i * 11) % 6) * 0.08,
      })),
    []
  );
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
      {stars.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full bg-primary"
          style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, opacity: s.opacity }}
        />
      ))}
    </div>
  );
};

// Interactive parallax stars that move against cursor movement for depth
export const InteractiveStars = () => {
  const lowPower = useLowPower();
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  // Create three layers of stars with different parallax speeds
  // Layer 1: Slowest (farthest away)
  const layer1X = useTransform(mouseX, [0, 1], [20, -20]);
  const layer1Y = useTransform(mouseY, [0, 1], [20, -20]);
  
  // Layer 2: Medium speed (middle distance)
  const layer2X = useTransform(mouseX, [0, 1], [40, -40]);
  const layer2Y = useTransform(mouseY, [0, 1], [40, -40]);
  
  // Layer 3: Fastest (closest)
  const layer3X = useTransform(mouseX, [0, 1], [60, -60]);
  const layer3Y = useTransform(mouseY, [0, 1], [60, -60]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      mouseX.set(x);
      mouseY.set(y);
    };

    if (lowPower) return;
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY, lowPower]);

  // Generate star positions for each layer with independent movement data
  const generateStars = (count: number, seed: number) => {
    return Array.from({ length: count }, (_, i) => ({
      id: `${seed}-${i}`,
      x: ((i * 37 + seed * 17) % 100),
      y: ((i * 53 + seed * 23) % 100),
      size: 1 + ((i * 7) % 3) * 0.5,
      opacity: 0.3 + ((i * 11) % 7) * 0.1,
      // Independent movement parameters - much larger range
    }));
  };

  const layer1Stars = generateStars(30, 1);
  const layer2Stars = generateStars(25, 2);
  const layer3Stars = generateStars(20, 3);

  if (lowPower) return <StaticStars />;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Layer 1: Background stars (slowest, smallest) */}
      <motion.div
        className="absolute inset-0"
        style={{ x: layer1X, y: layer1Y }}
      >
        {layer1Stars.map((star) => (
          <span
            key={star.id}
            className="absolute rounded-full"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: star.size,
              height: star.size,
              background: `hsl(var(--primary))`,
              opacity: star.opacity,
              boxShadow: `0 0 ${star.size * 2}px hsl(var(--primary) / 0.5)`,
            }}
          />
        ))}
      </motion.div>

      {/* Layer 2: Middle stars (medium speed) */}
      <motion.div
        className="absolute inset-0"
        style={{ x: layer2X, y: layer2Y }}
      >
        {layer2Stars.map((star) => (
          <span
            key={star.id}
            className="absolute rounded-full"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: star.size,
              height: star.size,
              background: `hsl(var(--accent))`,
              opacity: star.opacity,
              boxShadow: `0 0 ${star.size * 2}px hsl(var(--accent) / 0.5)`,
            }}
          />
        ))}
      </motion.div>

      {/* Layer 3: Foreground stars (fastest, brightest) */}
      <motion.div
        className="absolute inset-0"
        style={{ x: layer3X, y: layer3Y }}
      >
        {layer3Stars.map((star) => (
          <span
            key={star.id}
            className="absolute rounded-full"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: star.size + 0.5,
              height: star.size + 0.5,
              background: `hsl(var(--cta-primary))`,
              opacity: star.opacity + 0.1,
              boxShadow: `0 0 ${star.size * 3}px hsl(var(--cta-primary) / 0.6)`,
            }}
          />
        ))}
      </motion.div>
    </div>
  );
};
